import { Grid } from "@freightsimple/ui-core/PageLayout/Grid";
import { Panel } from "@freightsimple/ui-core/PageLayout/Panel";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import { type PerformanceBillingHygiene } from "@freightsimple/generated-carriers-openapi-client/models/PerformanceBillingHygiene";
import { type PerformanceCspUtilisation } from "@freightsimple/generated-carriers-openapi-client/models/PerformanceCspUtilisation";
import { formatPercent } from "@/App/Views/carrierFormatting";
import { PerformanceMetric } from "@/App/Performance/PerformanceMetric";

/**
 * How much hand-work this carrier's invoicing costs us, and whether the
 * customer-specific pricing we set up for them moves any freight.
 *
 * Every figure reads from the first invoice on each shipment only — later
 * corrections are the audit's business, not a measure of how the carrier bills.
 * Nothing here says which audit rule an invoice tripped or how we settled a
 * problem on our side.
 */
export function BillingHygienePanel({
  billingHygiene,
  cspUtilisation,
}: {
  billingHygiene: PerformanceBillingHygiene | undefined;
  cspUtilisation: PerformanceCspUtilisation | undefined;
}) {
  return (
    <Stack gap={4}>
      {billingHygiene && (
        <Panel
          title="Invoicing"
          description="From the first invoice on each shipment in this period."
        >
          <Grid columns={3} gap="md">
            <PerformanceMetric
              label="Arrived Electronically"
              value={formatRate_(billingHygiene.electronicRate)}
              hint={`of ${billingHygiene.firstInvoices.toLocaleString("en-CA")} invoices`}
              explanation="The share of first invoices that reached us through EDI or an API rather than as email or paper. Only the first invoice on each shipment counts; later corrections belong to the audit, not to how you bill."
            />
            <PerformanceMetric
              label="Approved Automatically"
              value={formatRate_(billingHygiene.autoApprovalRate)}
              hint="no one here had to review it"
              explanation="The share of first invoices that matched the agreed rate closely enough to be approved without anyone here opening them. Measured over the invoices where an approval decision was actually reached."
            />
            <PerformanceMetric
              label="Audit Problems"
              value={formatRate_(billingHygiene.carrierFaultAuditRate)}
              hint="resolved, and attributed to you"
              explanation="The share of shipments in this window where we opened an invoice audit, finished it, and concluded the difference was yours. Audits still open are excluded, as are ones resolved as our own or the customer's."
            />
          </Grid>
          {/* Said explicitly because it is the number most likely to prompt a
              reply: we do not count anything still under discussion. */}
          <T size="sm" color="muted">
            Audit problems count only shipments where we finished looking and
            concluded the difference was yours. Anything still open isn&rsquo;t
            counted against you.
          </T>
        </Panel>
      )}

      {cspUtilisation && cspUtilisation.liveAgreements > 0 && (
        <Panel
          title="Customer Specific Pricing"
          description="Your own accounts, and whether they're moving freight."
        >
          <Grid columns={3} gap="md">
            <PerformanceMetric
              label="Live Accounts"
              value={cspUtilisation.liveAgreements.toLocaleString("en-CA")}
              explanation="Customer-specific pricing agreements of yours that are live right now — a count as of today, not something measured over the window."
            />
            <PerformanceMetric
              label="Shipping"
              value={cspUtilisation.shippingAgreements.toLocaleString("en-CA")}
              hint="had a booking in this period"
              explanation="How many of those live accounts actually produced at least one booking with you inside the selected window."
            />
            <PerformanceMetric
              label="Utilization"
              value={formatRate_(cspUtilisation.utilisationRate)}
              hint="of your live accounts"
              explanation="Shipping divided by Live Accounts: the share of your live customer-specific pricing agreements that moved any freight in this window."
            />
          </Grid>
          <T size="sm" color="muted">
            An account that&rsquo;s live but not shipping usually means our
            customer isn&rsquo;t seeing you win on price. Your price position is
            on the Price Position tab.
          </T>
        </Panel>
      )}
    </Stack>
  );
}

// An absent rate means an empty denominator, not zero.
function formatRate_(value: number | undefined): string {
  return value === undefined ? "—" : formatPercent(value);
}
